/**
 * Jolis Crus — <jc-search>
 *
 * Powers both the desktop inline field and the mobile modal instance of
 * jc-header-search.liquid. Always AJAX — the wrapping <form> is never
 * allowed to submit natively, including on Enter, regardless of
 * settings.predictive_search_enabled (that global setting is intentionally
 * ignored for this component).
 *
 * Three merged result sections:
 * - Suggestions (vigneron collections): filtered synchronously, client-side,
 *   against a JSON payload pre-loaded once (snippets/jc-vigneron-search-data),
 *   since Shopify's Predictive Search API can match collections by
 *   title/handle only, not by metafield.
 * - Produits / Pages: fetched from Shopify's predictive-search Section
 *   Rendering endpoint (sections/predictive-search.liquid, trimmed to just
 *   these two resource types).
 *
 * Reuses debounce()/trapFocus()/removeTrapFocus() from global.js.
 */

(function () {
  function normalize(value) {
    return value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  }

  // Shopify's `t` filter HTML-escapes translation output (e.g. "l'orthographe"
  // becomes "l&#39;orthographe"), which is correct when echoed into markup but
  // wrong for strings consumed here as plain text/JS. Decode once on read.
  function decodeHtmlEntities(value) {
    const el = document.createElement('textarea');
    el.innerHTML = value;
    return el.value;
  }

  function loadVigneronData() {
    if (JcSearch.vignerons) return JcSearch.vignerons;
    const dataEl = document.getElementById('jc-vigneron-search-data');
    try {
      JcSearch.vignerons = dataEl ? JSON.parse(dataEl.textContent) : [];
    } catch (error) {
      JcSearch.vignerons = [];
    }
    return JcSearch.vignerons;
  }

  function matchVignerons(term) {
    const normTerm = normalize(term);
    return loadVigneronData().filter((vigneron) => normalize(vigneron.title).includes(normTerm));
  }

  class JcSearch extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('input[type="search"]');
      this.form = this.input.closest('form');
      this.resetButton = this.querySelector('button[type="reset"]');
      this.resultsRoot = this.querySelector('.jc-search-results');
      this.idPrefix = this.resultsRoot.id.replace(/-results$/, '');

      this.sections = {
        suggestions: this.querySelector('[data-jc-search-section="suggestions"]'),
        products: this.querySelector('[data-jc-search-section="products"]'),
        pages: this.querySelector('[data-jc-search-section="pages"]'),
      };
      this.lists = {
        suggestions: this.sections.suggestions.querySelector('.jc-search-section__list'),
        products: this.sections.products.querySelector('.jc-search-section__list'),
        pages: this.sections.pages.querySelector('.jc-search-section__list'),
      };
      this.templates = {
        suggestions: document.getElementById(this.idPrefix + '-suggestion-template'),
        products: document.getElementById(this.idPrefix + '-product-template'),
        pages: document.getElementById(this.idPrefix + '-page-template'),
      };
      this.emptyEl = document.getElementById(this.idPrefix + '-empty');
      this.statusEl = document.getElementById(this.idPrefix + '-status');
      this.popoverEl = this.querySelector('[data-jc-search-popover]');

      this.isOpen = false;
      this.searchTerm = '';
      this.loadingText = this.getAttribute('data-loading-text') || '';
      this.abortController = new AbortController();

      this.debouncedInput = debounce(this.onInput.bind(this), 300);
      this.setupEventListeners();
    }

    setupEventListeners() {
      this.form.addEventListener('submit', (event) => event.preventDefault());
      this.input.addEventListener('input', this.debouncedInput);
      this.input.addEventListener('focus', this.onFocus.bind(this));
      this.addEventListener('focusout', this.onFocusOut.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));
      if (this.resetButton) this.resetButton.addEventListener('click', this.onReset.bind(this));
    }

    getQuery() {
      return this.input.value.trim();
    }

    toggleResetButton(term) {
      if (!this.resetButton) return;
      this.resetButton.classList.toggle('hidden', term.length === 0);
    }

    onInput() {
      const term = this.getQuery();
      this.toggleResetButton(term);
      this.searchTerm = term;

      if (!term.length) {
        this.close();
        return;
      }

      this.runSearch(term);
    }

    onFocus() {
      const term = this.getQuery();
      if (term.length && term === this.searchTerm) this.open();
    }

    onFocusOut() {
      setTimeout(() => {
        if (!this.contains(document.activeElement)) this.close();
      });
    }

    onReset(event) {
      event.preventDefault();
      this.abortController.abort();
      this.input.value = '';
      this.searchTerm = '';
      this.toggleResetButton('');
      this.close();
      this.input.focus();
    }

    onKeydown(event) {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.switchOption('down');
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.switchOption('up');
          break;
        case 'Enter':
          event.preventDefault();
          this.selectOption();
          break;
        case 'Escape':
          this.close();
          this.input.focus();
          break;
      }
    }

    switchOption(direction) {
      if (!this.isOpen) return;

      const allVisible = Array.from(this.querySelectorAll('.jc-search-result')).filter(
        (element) => element.offsetParent !== null
      );
      if (!allVisible.length) return;

      const current = this.querySelector('.jc-search-result[aria-selected="true"]');
      let index = allVisible.indexOf(current);

      if (direction === 'down') {
        index = index === -1 || index === allVisible.length - 1 ? 0 : index + 1;
      } else {
        index = index <= 0 ? allVisible.length - 1 : index - 1;
      }

      if (current) current.setAttribute('aria-selected', 'false');
      const next = allVisible[index];
      next.setAttribute('aria-selected', 'true');
      this.input.setAttribute('aria-activedescendant', next.id || '');
    }

    selectOption() {
      const selected = this.querySelector('.jc-search-result[aria-selected="true"] a');
      if (selected) selected.click();
    }

    runSearch(term) {
      this.abortController.abort();
      this.abortController = new AbortController();

      const vignerons = matchVignerons(term).slice(0, 4);
      this.renderSection('suggestions', vignerons);
      this.open();
      this.updateLoadingState();

      this.fetchProductsAndPages(term, this.abortController.signal)
        .then(({ products, pages }) => {
          if (term !== this.searchTerm) return;
          this.renderSection('products', products.slice(0, 6));
          this.renderSection('pages', pages.slice(0, 4));
          this.finalizeResults(term, vignerons.length, products.length, pages.length);
        })
        .catch((error) => {
          if (error.name === 'AbortError') return;
          if (term !== this.searchTerm) return;
          this.renderSection('products', []);
          this.renderSection('pages', []);
          this.finalizeResults(term, vignerons.length, 0, 0);
        });
    }

    fetchProductsAndPages(term, signal) {
      const cacheKey = normalize(term);

      if (JcSearch.fetchCache.has(cacheKey)) {
        return Promise.resolve(JcSearch.fetchCache.get(cacheKey));
      }

      const url =
        window.routes.predictive_search_url +
        '?q=' +
        encodeURIComponent(term) +
        '&section_id=predictive-search&resources[type]=product,page&resources[limit]=8';

      return fetch(url, { signal })
        .then((response) => {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        })
        .then((text) => {
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const products = Array.from(doc.querySelectorAll('#jc-predictive-search-products li')).map((li) => ({
            title: li.dataset.title,
            url: li.dataset.url,
            image: li.dataset.image || '',
          }));
          const pages = Array.from(doc.querySelectorAll('#jc-predictive-search-pages li')).map((li) => ({
            title: li.dataset.title,
            url: li.dataset.url,
          }));
          const result = { products, pages };
          JcSearch.fetchCache.set(cacheKey, result);
          return result;
        });
    }

    renderSection(name, items) {
      const list = this.lists[name];
      const template = this.templates[name];
      list.innerHTML = '';

      items.forEach((item, index) => {
        const li = template.content.firstElementChild.cloneNode(true);
        li.id = this.idPrefix + '-' + name + '-' + index;
        li.querySelector('a').href = item.url;
        li.querySelector('.jc-search-result__text').textContent = item.title;

        if (name === 'products') {
          const img = li.querySelector('img');
          if (item.image) {
            img.src = item.image;
            img.alt = item.title;
          } else {
            img.closest('.jc-search-result__image-wrap').remove();
          }
        }

        list.appendChild(li);
      });

      this.sections[name].hidden = items.length === 0;
    }

    updateLoadingState() {
      const anyVisible = !this.sections.suggestions.hidden;
      if (anyVisible) {
        this.removeAttribute('loading');
      } else {
        this.setAttribute('loading', '');
        this.setLiveRegion(this.loadingText);
      }
    }

    finalizeResults(term, suggestionCount, productCount, pageCount) {
      this.removeAttribute('loading');
      const total = suggestionCount + productCount + pageCount;
      const strings = window.jcSearchStrings || {};
      this.emptyEl.hidden = total > 0;

      if (total === 0) {
        const message = decodeHtmlEntities((strings.noResults || '').replace('{{terms}}', term));
        this.emptyEl.textContent = message;
        this.setLiveRegion(message);
        return;
      }

      const countText = total === 1 ? strings.resultsOne : (strings.resultsOther || '').replace('2', String(total));
      this.setLiveRegion(decodeHtmlEntities(countText || ''));
    }

    setLiveRegion(text) {
      if (!this.statusEl) return;
      this.statusEl.textContent = text;
    }

    open() {
      if (this.popoverEl) this.popoverEl.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.isOpen = true;
    }

    close() {
      if (this.popoverEl) this.popoverEl.hidden = true;
      const selected = this.querySelector('.jc-search-result[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', 'false');
      this.input.setAttribute('aria-activedescendant', '');
      this.input.setAttribute('aria-expanded', 'false');
      this.removeAttribute('loading');
      this.isOpen = false;
    }
  }

  JcSearch.vignerons = null;
  JcSearch.fetchCache = new Map();

  customElements.define('jc-search', JcSearch);
})();
